/**
 * SuggestionCard - individual suggestion entry with flashcard, navigation,
 * freshness timestamp, and slide animation support.
 *
 * Extracted from SuggestionsWidget for file-size management.
 */

import React, { useRef, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { Badge } from './Badge';
import { Button } from './Button';
import type {
  Flashcard as CoachingFlashcard,
  GateUrgency,
  RiskSignal,
  KnowledgeBaseReference,
} from '@revont/shared-types';
import { Flashcard } from './Flashcard';
import { getKnowledgeBaseReferenceBadgeVariant } from './knowledge-base-reference-style';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SuggestionEntry {
  flashcard: CoachingFlashcard;
  urgency: GateUrgency;
  riskSignals?: RiskSignal[];
  timestamp: number;
  knowledgeBaseReferences?: KnowledgeBaseReference[];
}

export type SlideDirection = 'left' | 'right' | 'new';

export interface SuggestionCardProps {
  suggestion: SuggestionEntry;
  direction: SlideDirection;
  flashcardVariants: Variants;
  freshnessText: string;
  freshnessOpacity: number;
  historyIndex: number;
  historyLength: number;
  onPrev: () => void;
  onNext: () => void;
  onAssist?: () => void;
  assistDisabled?: boolean;
  /** Suggestion mode - drives the Flashcard header label. */
  mode?: 'teleprompter' | 'topic';
  /** Whether the live suggestion was used (from usage tracking). */
  used?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SuggestionCard = React.memo(function SuggestionCard({
  suggestion,
  direction,
  flashcardVariants,
  freshnessText,
  freshnessOpacity,
  historyIndex,
  historyLength,
  onPrev,
  onNext,
  onAssist,
  assistDisabled,
  mode,
  used,
}: SuggestionCardProps) {
  const regionRef = useRef<HTMLDivElement>(null);
  const hasMultiple = historyLength > 1;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!hasMultiple) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      }
    },
    [hasMultiple, onPrev, onNext]
  );

  return (
    <div
      ref={regionRef}
      role="region"
      aria-live="polite"
      aria-label="Conversation suggestions. Use left and right arrow keys to navigate."
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="outline-none"
      style={{ opacity: freshnessOpacity }}
    >
      <div className="relative">
        <AnimatePresence custom={direction} initial={false} mode="wait">
          <motion.div
            key={suggestion.flashcard.id}
            custom={direction}
            variants={flashcardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Flashcard
              category={suggestion.flashcard.category}
              content={suggestion.flashcard.content}
              isLoading={false}
              urgency={suggestion.urgency}
              riskSignals={suggestion.riskSignals}
              clientTone={suggestion.flashcard.clientTone}
              mode={mode}
              headerRight={
                <div className="flex items-center gap-2">
                  {used && (
                    <span className="text-green-400 text-[10px] px-1 bg-green-500/20 rounded whitespace-nowrap select-none">
                      Used
                    </span>
                  )}
                  {hasMultiple && (
                    <>
                      <Button
                        variant="ghost"
                        size="small"
                        iconPosition="icon-only"
                        onClick={(e) => { e?.stopPropagation(); onPrev(); }}
                        disabled={historyIndex >= historyLength - 1}
                        ariaLabel="Older suggestion"
                        icon={<ChevronLeftIcon className="w-3 h-3 text-white/60" />}
                        className="!w-5 !h-5 !bg-white/5 hover:!bg-white/15 !p-0 !min-w-0"
                      />
                      <span className="text-[10px] text-white/50 tabular-nums select-none">
                        {historyIndex + 1}/{historyLength}
                      </span>
                      <Button
                        variant="ghost"
                        size="small"
                        iconPosition="icon-only"
                        onClick={(e) => { e?.stopPropagation(); onNext(); }}
                        disabled={historyIndex <= 0}
                        ariaLabel="Newer suggestion"
                        icon={<ChevronRightIcon className="w-3 h-3 text-white/60" />}
                        className="!w-5 !h-5 !bg-white/5 hover:!bg-white/15 !p-0 !min-w-0"
                      />
                    </>
                  )}
                  {freshnessText && (
                    <>
                      {hasMultiple && <span className="text-white/15">|</span>}
                      <span className="text-[10px] text-white/35 select-none whitespace-nowrap">
                        {freshnessText}
                      </span>
                    </>
                  )}
                  {onAssist && (
                    <>
                      <span className="text-white/15">|</span>
                      <Button
                        variant="ghost"
                        size="small"
                        icon={<SparklesIcon className="w-3 h-3 text-white/60" />}
                        onClick={(e) => { e?.stopPropagation(); onAssist(); }}
                        disabled={assistDisabled}
                        ariaLabel="Request AI coaching assist"
                        className="!h-5 !bg-white/5 hover:!bg-white/15 !px-2 !py-0 !min-w-0 !text-[10px] !text-white/50 hover:!text-white/70 !gap-1"
                      >
                        Assist
                      </Button>
                    </>
                  )}
                </div>
              }
            />
            {suggestion.knowledgeBaseReferences && suggestion.knowledgeBaseReferences.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 px-1 pt-2" aria-label="Knowledge Base References">
                <span className="text-[10px] text-white/35">Sources</span>
                {suggestion.knowledgeBaseReferences.slice(0, 3).map((reference) => (
                  <span
                    key={reference.articleId}
                    title={`${reference.title}${reference.tags.length > 0 ? ` - ${reference.tags.join(', ')}` : ''}`}
                    className="max-w-[220px]"
                  >
                    <Badge
                      variant={getKnowledgeBaseReferenceBadgeVariant(reference.tags)}
                      size="sm"
                      bordered
                      className="max-w-full truncate"
                    >
                      {reference.title}
                    </Badge>
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
});
